/**
 * Charts row: bar horizontal (top apps) + donut (DENY reasons).
 *
 * Uses inline SVG for both charts to avoid pulling extra libs (recharts is
 * available in the project but @mantine/charts is the preferred path; we use
 * a simple custom-painted approach so the visuals match the mockup tightly
 * and tests can assert on plain text/aria labels).
 */
import { Card, Group, SimpleGrid, Stack, Text, Title } from '@mantine/core'
import type { AccessStatsDeny, AccessStatsTopApp } from '../types'
import styles from '../ZtnaAdmin.module.css'

interface Props {
  topApps: AccessStatsTopApp[]
  denyReasons: AccessStatsDeny[]
  isLoading?: boolean
}

const DENY_COLORS: Record<string, string> = {
  tenant_scope: '#f59f00',
  rbac_missing: '#e03131',
  mfa_required: '#7950f2',
  unknown: '#5f7882',
}

function colorFor(reason: string, idx: number): string {
  const fixed = DENY_COLORS[reason]
  if (fixed) return fixed
  const palette = ['#098d93', '#025468', '#7950f2', '#f59f00', '#e03131', '#5f7882']
  return palette[idx % palette.length]
}

export function ChartsRow({ topApps, denyReasons, isLoading }: Props) {
  // top_apps vem ordenado decrescente do backend. Removido o fallback
  // antigo que usava last_latency_ms (ms!) como se fosse contagem de
  // acessos — gerava numeros enganosos quando audit estava vazio.
  const maxCount = topApps.reduce((m, r) => Math.max(m, r.accesses), 0) || 1

  // Slug duplicado quando varias apps compartilham slug em tenants/envs
  // distintos: marcamos pra mostrar label com sufixo (env).
  const slugCount = topApps.reduce<Record<string, number>>((acc, r) => {
    acc[r.slug] = (acc[r.slug] ?? 0) + 1
    return acc
  }, {})

  const totalDeny = denyReasons.reduce((s, r) => s + r.count, 0)

  return (
    <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
      <Card>
        <Group justify="space-between" mb="sm">
          <Title order={5} c="var(--ai-brand-azul)">
            Top apps por acessos
          </Title>
          <Text size="xs" c="dimmed">
            últimas 24h
          </Text>
        </Group>
        {isLoading ? (
          <Text size="sm" c="dimmed">Carregando…</Text>
        ) : topApps.length === 0 ? (
          <Text size="sm" c="dimmed">
            Sem acessos nas últimas 24h
          </Text>
        ) : (
          <Stack gap={2}>
            {topApps.map((row) => {
              const pct = Math.max(2, Math.round((row.accesses / maxCount) * 100))
              const ambiguous = (slugCount[row.slug] ?? 0) > 1
              const label = ambiguous
                ? `${row.slug} (${row.environment})`
                : row.slug
              const tooltip = `${row.display_name} — ${row.slug} · tenant ${row.tenant_id} · ${row.environment}`
              return (
                <div key={row.application_id} className={styles.barRow}>
                  <span className={styles.barLabel} title={tooltip}>
                    {label}
                  </span>
                  <span className={styles.barTrack}>
                    <span
                      className={styles.barFill}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className={styles.barVal}>{row.accesses}</span>
                </div>
              )
            })}
          </Stack>
        )}
      </Card>

      <Card>
        <Group justify="space-between" mb="sm">
          <Title order={5} c="var(--ai-brand-azul)">
            DENY reasons
          </Title>
          <Text size="xs" c="dimmed">24h</Text>
        </Group>
        {isLoading ? (
          <Text size="sm" c="dimmed">Carregando…</Text>
        ) : denyReasons.length === 0 ? (
          <Text size="sm" c="dimmed">Nenhum DENY registrado</Text>
        ) : (
          <Group align="center" gap="md">
            <DonutSvg
              segments={denyReasons.map((r, i) => ({
                value: r.count,
                color: colorFor(r.reason, i),
              }))}
              total={totalDeny}
            />
            <Stack gap={6} style={{ flex: 1 }}>
              {denyReasons.map((r, i) => (
                <Group key={r.reason} gap="xs" wrap="nowrap">
                  <span
                    aria-hidden
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      background: colorFor(r.reason, i),
                      flexShrink: 0,
                    }}
                  />
                  <Text size="sm" style={{ flex: 1 }}>
                    {r.reason}
                  </Text>
                  <Text size="sm" fw={700} ff="monospace">
                    {r.count}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Group>
        )}
      </Card>
    </SimpleGrid>
  )
}

interface DonutProps {
  segments: { value: number; color: string }[]
  total: number
}

function DonutSvg({ segments, total }: DonutProps) {
  const radius = 15.9
  const circumference = 2 * Math.PI * radius
  let acc = 0
  const safeTotal = total > 0 ? total : 1
  return (
    <svg
      viewBox="0 0 36 36"
      width={120}
      height={120}
      role="img"
      aria-label={`donut total ${total}`}
    >
      <circle
        cx={18}
        cy={18}
        r={radius}
        fill="none"
        stroke="#e6f3f5"
        strokeWidth={3.5}
      />
      {segments.map((seg, i) => {
        const len = (seg.value / safeTotal) * 100
        const offset = -acc
        acc += len
        return (
          <circle
            key={i}
            cx={18}
            cy={18}
            r={radius}
            fill="none"
            stroke={seg.color}
            strokeWidth={3.5}
            strokeDasharray={`${len} 100`}
            strokeDashoffset={offset}
            transform="rotate(-90 18 18)"
            style={{ transition: 'stroke-dasharray 0.3s ease' }}
          />
        )
      })}
      <text
        x={18}
        y={19}
        textAnchor="middle"
        fill="#025468"
        fontSize={6}
        fontWeight={700}
        fontFamily="JetBrains Mono"
      >
        {total}
      </text>
      <text
        x={18}
        y={24}
        textAnchor="middle"
        fill="#94aab3"
        fontSize={2.4}
        fontFamily="Barlow"
      >
        DENY
      </text>
    </svg>
  )
}

export default ChartsRow
